import { useEffect, useState, useRef } from "react";
import qrImage from "../../assets/qrimage.png";
import AxiosWrapper from "../../../basecomp/base/AxiosWrapper";
import {
    Button,
    MenuItem,
    Select,
    Box,
    Paper,
    Typography,
    FormControl,
    Checkbox,
    FormControlLabel,
    FormGroup,
    TextField,
    Grid,
    Divider,
    Stack,
    Chip,
} from "@mui/material";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import { Rnd } from "react-rnd";

const SCALE = 8; // px per unit for the sticker preview

// Logo upload constraints
const LOGO_MAX_SIZE = 500 * 1024; // 500KB hard limit — reject above this
const LOGO_COMPRESS_THRESHOLD = 100 * 1024; // compress if raw file is above this
const LOGO_COMPRESS_QUALITY = 0.5; // ~50% compression

function WarehouseConfig() {
    const [sizes, setSizes] = useState([]);
    const [selectedSize, setSelectedSize] = useState("");
    const { handleToastOpen } = useToast()
    const [checkboxList, setCheckBoxList] = useState({
        storage_bin_loc: false,
        qrcode: false,
        logo: false,
    });

    const [selectedField, setSelectedField] = useState(null);

    const [configuration, setConfiguration] = useState({});

    const fileInputRef = useRef(null);

    const [stickerData, setStickerData] = useState({
        storage_bin_loc: "WF08-04F1",
    });

    // Default positions expressed as FRACTIONS (0–1) of the sticker's
    // width/height so every field stays inside the canvas regardless of
    // which size is selected.
    const defaultPositionTemplate = {
        storage_bin_loc: { x: 0.30, y: 0.35, width: 0.45, height: 0.30 },
        qrcode: { x: 0.02, y: 0.02, width: 0.20, height: 0.20 },
    };

    // Turns a field's stored fraction into real x/y/width/height/fontsize for
    // the currently selected sticker size. fontsize scales off the shorter
    // side so text doesn't blow up on a wide-but-short label.
    const getDefaultPosition = (item, size) => {
        if (item === "logo") return getLogoDefaultPosition(size);

        const t = defaultPositionTemplate[item];
        if (!t) return null;
        if (!size) {
            return { x: 5, y: 5, width: 20, height: 10, fontsize: 2 };
        }

        const minDim = Math.min(size.width, size.height);

        return {
            x: Math.round(t.x * size.width),
            y: Math.round(t.y * size.height),
            width: Math.round(t.width * size.width),
            height: Math.round(t.height * size.height),
            fontsize: Math.max(1, Math.round(0.05 * minDim)),
        };
    };

    // Clamps a whole box so it can never sit off the sticker: x/y can't go
    // negative or push the box past the sticker's edge, and width/height
    // can't exceed what's left of the sticker from that x/y. Order matters —
    // width/height are capped to the sticker first, then x/y are capped to
    // what's left after that width/height, so the box always stays fully
    // visible instead of just clipping x to the sticker's outer edge.
    const clampBox = (box, size) => {
        if (!size) {
            return {
                x: Math.max(0, box.x),
                y: Math.max(0, box.y),
                width: Math.max(0, box.width),
                height: Math.max(0, box.height),
            };
        }
        const width = Math.min(Math.max(0, box.width), size.width);
        const height = Math.min(Math.max(0, box.height), size.height);
        const x = Math.min(Math.max(0, box.x), Math.max(0, size.width - width));
        const y = Math.min(Math.max(0, box.y), Math.max(0, size.height - height));
        return { x, y, width, height };
    };

    const Base_URL = process.env.REACT_APP_BE_URL;

    // Logo has no fixed default — it's computed relative to the selected
    // sticker's width/height so it always lands fully inside the canvas.
    const getLogoDefaultPosition = (size) => {
        if (!size) {
            return { x: 5, y: 5, width: 20, height: 20, fontsize: 2 };
        }
        const width = Math.max(10, Math.round(size.width * 0.25));
        const height = Math.max(10, Math.round(size.height * 0.25));
        const x = Math.round((size.width - width) / 2);
        const y = Math.round((size.height - height) / 2);
        return { x, y, width, height, fontsize: 2 };
    };

    // Redraws the image through a canvas and re-exports as JPEG at the given
    // quality to shrink file size before it goes into configuration.
    const compressImage = (dataUrl, quality = LOGO_COMPRESS_QUALITY) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL("image/jpeg", quality));
            };
            img.onerror = reject;
            img.src = dataUrl;
        });

    useEffect(() => {
        const getSizes = async () => {
            try {
                const response = await AxiosWrapper.axiosGetCallWithToken(
                    `${Base_URL}/label_print/sizes`
                );

                const sizeData = response.data || [];

                setSizes(sizeData);

                if (sizeData.length === 1) {
                    setSelectedSize(sizeData[0]);
                }
            } catch (error) {
                console.log("Error fetching sizes:", error);
                handleToastOpen("error", error)

            }
        };

        getSizes();
    }, []);

    useEffect(() => {
        if (selectedSize === "") {
            return
        }
        const getConfig = async () => {
            try {
                const response = await AxiosWrapper.axiosGetCallWithToken(
                    `${Base_URL}/label_print/config?id=${selectedSize.id}&type=warehouse`
                );
                const configData = response.data || {};

                if (Object.keys(configData).length !== 0) {
                    const updated = Object.fromEntries(
                        Object.keys(checkboxList).map((key) => [
                            key,
                            key in configData
                        ])
                    );

                    setCheckBoxList(updated);
                    setConfiguration(configData)
                }
                else {
                    setConfiguration(configData)
                    setCheckBoxList((prev) => {
                        let updated = {}
                        Object.keys(prev).forEach(elem => {
                            updated[elem] = false;
                        })
                        return updated
                    })
                }
                setSelectedField(null)

            } catch (error) {
                console.log("Error fetching sizes:", error);
                handleToastOpen("error", error)

            }
        };
        getConfig()
    }, [selectedSize])

    const handelSizeChange = (e) => {
        setSelectedSize(e.target.value);
    };

    // While typing, just store what's in the box — including "" while it's
    // cleared. Clamping here on every keystroke was forcing the field back to
    // 0 the moment it was empty, making it impossible to clear and retype.
    const handelFieldEdit = (attribute, e) => {
        const value = e.target.value;

        if (attribute === 'text') {
            setStickerData((prev) => ({
                ...prev,
                [selectedField]: value
            }));
        }

        setConfiguration((prev) => ({
            ...prev,
            [selectedField]: {
                ...prev[selectedField],
                [attribute]: value,
            },
        }));
    };

    // Once the user leaves x/y/width/height/fontsize, finalize it: fall back
    // to 0 (or 1 for fontsize) if it was left empty or invalid, then clamp
    // against the sticker's dimensions.
    const handelFieldBlur = (attribute) => {
        if (!["x", "y", "width", "height", "fontsize"].includes(attribute)) return;

        setConfiguration((prev) => {
            const field = prev[selectedField];
            if (!field) return prev;

            if (attribute === "fontsize") {
                let fontsize = Number(field.fontsize);
                if (Number.isNaN(fontsize)) fontsize = 1;
                fontsize = Math.max(1, fontsize);
                return {
                    ...prev,
                    [selectedField]: { ...field, fontsize },
                };
            }

            const box = clampBox(
                {
                    x: Number(field.x) || 0,
                    y: Number(field.y) || 0,
                    width: Number(field.width) || 0,
                    height: Number(field.height) || 0,
                },
                selectedSize
            );

            return {
                ...prev,
                [selectedField]: { ...field, ...box },
            };
        });
    };

    const handleCheckBoxChange = (item) => {
        // Logo doesn't have a data field to fall back on, so checking it opens
        // the file picker instead of writing a default (imageless) config.
        if (item === "logo") {
            if (checkboxList.logo) {
                setSelectedField(null);
                setCheckBoxList((prev) => ({ ...prev, logo: false }));
                setConfiguration((prev) => {
                    const { logo: _, ...rest } = prev;
                    return rest;
                });
            } else {
                fileInputRef.current.click();
            }
            return;
        }

        setSelectedField(null)
        setCheckBoxList((prev) => ({
            ...prev,
            [item]: !prev[item]
        }));

        setConfiguration((prev) => {
            if (prev[item]) {
                const { [item]: _, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [item]: getDefaultPosition(item, selectedSize)
            };
        });

    };

    const handleLogoFileChange = async (e) => {
        const file = e.target.files[0];
        e.target.value = ""; // reset so re-selecting the same file still fires onChange

        if (!file) return;

        if (file.size > LOGO_MAX_SIZE) {
            handleToastOpen("error", "Logo image must be under 500KB");
            return;
        }

        try {
            const dataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });

            const finalImage =
                file.size > LOGO_COMPRESS_THRESHOLD
                    ? await compressImage(dataUrl, LOGO_COMPRESS_QUALITY)
                    : dataUrl;

            const position = getDefaultPosition("logo", selectedSize);

            setConfiguration((prev) => ({
                ...prev,
                logo: {
                    ...position,
                    image: finalImage,
                },
            }));
            setCheckBoxList((prev) => ({ ...prev, logo: true }));
            setSelectedField("logo");
        } catch (error) {
            console.log("Error processing logo image:", error);
            handleToastOpen("error", "Failed to process logo image");
        }
    };

    const handelSaveClick = async () => {
        let formdata = new FormData()
        formdata.append("sizeid", selectedSize.id)
        formdata.append("type", "warehouse")
        formdata.append("config", JSON.stringify(configuration))
        let response = await AxiosWrapper.axiosPostCallWithToken(`${Base_URL}/label_print/saveConfig`, formdata)
        handleToastOpen("success", response.data.message)
    }

    const fieldType = (elem) => {
        if (elem === "qrcode") {
            return <img
                src={qrImage}
                alt="QR Code"
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "fill",
                    border: "1px dashed #999",
                    boxSizing: "border-box",
                    cursor: "move",
                    margin: 0,
                    padding: 0,
                    display: "block",
                    userSelect: "none",
                }}
            />
        }
        else if (elem === "logo") {
            return <img
                src={configuration[elem].image}
                alt="Logo"
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "fill",
                    border: selectedField === elem ? "1px solid #1976d2" : "1px dashed #999",
                    background: selectedField === elem ? "rgba(25,118,210,0.06)" : "transparent",
                    boxSizing: "border-box",
                    cursor: "move",
                    margin: 0,
                    padding: 0,
                    display: "block",
                    userSelect: "none",
                }}
            />
        }
        else {
            return <div
                style={{
                    width: "100%",
                    height: "100%",
                    cursor: "move",
                    border: selectedField === elem ? "1px solid #1976d2" : "1px dashed #999",
                    background: selectedField === elem ? "rgba(25,118,210,0.06)" : "transparent",
                    boxSizing: "border-box",
                    wordBreak: "break-all",
                    padding: 0,
                    fontSize: `${configuration[elem].fontsize * SCALE}px`,
                }}
            >
                {(configuration[elem].pretext ? configuration[elem].pretext : "") + (configuration[elem].text || stickerData[elem])}
            </div>
        }
    }

    // Human-friendly labels for the raw field keys
    const fieldLabels = {
        storage_bin_loc: "Storage Bin Location",
        qrcode: "QR Code",
        logo: "Logo",
    };

    return (
        <div
            style={{
                padding: "1rem",
            }}
        >
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleLogoFileChange}
            />
            <Stack spacing sx={{ width: "100%" }}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <FormControl sx={{ minWidth: 240 }} size="small">
                        <Select
                            labelId="warehouse-size-label"
                            displayEmpty
                            value={selectedSize}
                            onChange={handelSizeChange}
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <em>Select Size</em>;
                                }
                                return selected.size;
                            }}
                        >

                            {sizes.map((size) => (
                                <MenuItem key={size.size} value={size}>
                                    {size.size}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Paper>

                {selectedSize && (
                    <Grid container spacing={2}>
                        {/* Field selection */}
                        <Grid item xs={12} md={2}>
                            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    Fields
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                <FormGroup>
                                    {Object.keys(checkboxList).map((item) => (
                                        <FormControlLabel
                                            key={item}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={checkboxList[item]}
                                                    onChange={() => handleCheckBoxChange(item)}
                                                />
                                            }
                                            label={fieldLabels[item] || item}
                                        />
                                    ))}
                                </FormGroup>
                            </Paper>
                        </Grid>

                        {/* Canvas preview */}
                        <Grid item xs={12} md={8}>
                            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        Layout Preview
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={`${selectedSize.width} x ${selectedSize.height}`}
                                        variant="outlined"
                                    />
                                </Stack>

                                <Box
                                    sx={{
                                        width: "100%",
                                        overflow: "auto",
                                        display: "flex",
                                        justifyContent: "center",
                                        bgcolor: "grey.50",
                                        borderRadius: 1,
                                        py: 2,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: selectedSize.width * SCALE,
                                            height: selectedSize.height * SCALE,
                                            border: "2px solid #90a4ae",
                                            borderRadius: 4,
                                            position: "relative",
                                            flexShrink: 0,
                                            overflow: "hidden",

                                            backgroundColor: "#fff",

                                            // 1 mm grid
                                            backgroundImage: `
            linear-gradient(to right, #e6e6e6 1px, transparent 1px),
            linear-gradient(to bottom, #e6e6e6 1px, transparent 1px),

            linear-gradient(to right, #cfcfcf 1px, transparent 1px),
            linear-gradient(to bottom, #cfcfcf 1px, transparent 1px)
        `,

                                            // Minor grid = 1 mm
                                            // Major grid = 5 mm
                                            backgroundSize: `
            ${SCALE}px ${SCALE}px,
            ${SCALE}px ${SCALE}px,
            ${SCALE * 5}px ${SCALE * 5}px,
            ${SCALE * 5}px ${SCALE * 5}px
        `,
                                        }}
                                    >
                                        {Object.keys(configuration).map((elem) => (
                                            <Rnd
                                                key={elem}
                                                bounds="parent"
                                                dragGrid={[SCALE, SCALE]}
                                                resizeGrid={[SCALE, SCALE]}
                                                position={{
                                                    x: configuration[elem].x * SCALE,
                                                    y: configuration[elem].y * SCALE,
                                                }}
                                                size={{
                                                    width: configuration[elem].width * SCALE,
                                                    height: configuration[elem].height * SCALE,
                                                }}
                                                onMouseDown={() => setSelectedField(elem)}
                                                onDragStop={(e, d) => {
                                                    setSelectedField(elem)
                                                    setConfiguration((prev) => {
                                                        const field = prev[elem];
                                                        const box = clampBox(
                                                            { x: d.x / SCALE, y: d.y / SCALE, width: field.width, height: field.height },
                                                            selectedSize
                                                        );
                                                        return {
                                                            ...prev,
                                                            [elem]: { ...field, x: box.x, y: box.y },
                                                        };
                                                    });
                                                }}
                                                onResizeStop={(e, direction, ref, delta, position) => {
                                                    setSelectedField(elem)
                                                    setConfiguration((prev) => {
                                                        const field = prev[elem];
                                                        const box = clampBox(
                                                            {
                                                                x: position.x / SCALE,
                                                                y: position.y / SCALE,
                                                                width: ref.offsetWidth / SCALE,
                                                                height: ref.offsetHeight / SCALE,
                                                            },
                                                            selectedSize
                                                        );
                                                        return {
                                                            ...prev,
                                                            [elem]: { ...field, ...box },
                                                        };
                                                    });
                                                }}
                                            >
                                                {fieldType(elem)}
                                            </Rnd>
                                        ))}
                                    </div>
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Properties panel */}
                        <Grid item xs={12} md={2}>
                            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                                {selectedField ? (
                                    <>
                                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                            {fieldLabels[selectedField] || selectedField}
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        <Stack spacing={2}>
                                            <Stack direction="row" spacing={1}>
                                                <TextField
                                                    label="X"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    value={configuration[selectedField].x}
                                                    onChange={(e) => handelFieldEdit('x', e)}
                                                    onBlur={() => handelFieldBlur('x')}
                                                />
                                                <TextField
                                                    label="Y"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    value={configuration[selectedField].y}
                                                    onChange={(e) => handelFieldEdit('y', e)}
                                                    onBlur={() => handelFieldBlur('y')}
                                                />
                                            </Stack>
                                            <Stack direction="row" spacing={1}>
                                                <TextField
                                                    label="Width"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    value={configuration[selectedField].width}
                                                    onChange={(e) => handelFieldEdit('width', e)}
                                                    onBlur={() => handelFieldBlur('width')}
                                                />
                                                <TextField
                                                    label="Height"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    value={configuration[selectedField].height}
                                                    onChange={(e) => handelFieldEdit('height', e)}
                                                    onBlur={() => handelFieldBlur('height')}
                                                />
                                            </Stack>

                                            <TextField
                                                label="Font Size"
                                                type="number"
                                                size="small"
                                                value={configuration[selectedField].fontsize}
                                                onChange={(e) => handelFieldEdit('fontsize', e)}
                                                onBlur={() => handelFieldBlur('fontsize')}
                                            />
                                            {selectedField === "storage_bin_loc" && (
                                                <>
                                                    <TextField
                                                        label="Text"
                                                        size="small"
                                                        value={configuration[selectedField].text || stickerData[selectedField]}
                                                        onChange={(e) => handelFieldEdit('text', e)}
                                                    />
                                                    <TextField
                                                        label="Pre Text"
                                                        size="small"
                                                        value={configuration[selectedField].pretext || ""}
                                                        onChange={(e) => handelFieldEdit('pretext', e)}
                                                    />
                                                </>
                                            )}
                                            {selectedField === "logo" && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => fileInputRef.current.click()}
                                                >
                                                    Replace Image
                                                </Button>
                                            )}
                                        </Stack>
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                            Properties
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            Select a field on the sticker to edit its position, size, and text.
                                        </Typography>
                                    </>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {selectedSize && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button variant="contained" onClick={handelSaveClick}>
                            Save Configuration
                        </Button>
                    </Box>
                )}
            </Stack>
        </div>
    );
}

export default WarehouseConfig;